import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChakraUIProvider } from './components/ui/provider';
import { UserProvider } from './UserContext';
import { AuthProvider } from './AuthContext';
import { MoaniPriceProvider } from './contexts/MoaniPriceContext';
import { EnvironmentProvider } from './contexts/EnvironmentContext';

// Components
import Layout from './components/Layout';
import UserSearch from './components/UserSearch';
import UserManagement from './components/UserManagement';
import AssetsManager from './components/AssetsManager';
import Analytics from './components/Analytics';
import DatapointActivity from './components/DatapointActivity';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import AuthLogs from './components/AuthLogs';
import EventPlanner from './components/EventPlanner';
import EventDashboard from './components/EventDashboard';
import AdminUserManagement from './components/AdminUserManagement';
import MintRequestsManager from './components/MintRequestsManager';
import MintPipelineManager from './components/MintPipelineManager';
import ContractsManager from './components/ContractsManager';
import NftMetadataManager from './components/NftMetadataManager';
import Profile from './components/Profile';
import WalletManager from './components/WalletManager';
import SecurityManager from './components/SecurityManager';
import ServerDeployManager from './components/ServerDeployManager';
import ConversationDetail from './components/ConversationDetail';
import ConversationsList from './components/ConversationsList';
import StakesManager from './components/StakesManager';
import DropTableTester from './components/DropTableTester';
import AuctionRefunds from './components/AuctionRefunds';
import AllocationDashboard from './components/AllocationDashboard';
import TrackedWallets from './components/TrackedWallets';
import AuctionFarmers from './components/AuctionFarmers';
import MoaniBlockchainTransfers from './components/MoaniBlockchainTransfers';
import MoaniTransactions from './components/MoaniTransactions';
import MultiAccountDetection from './components/MultiAccountDetection';
import IPGroupedUsers from './components/IPGroupedUsers';
import QuestManager from './components/QuestManager';
import DailyQuestRewardRecovery from './components/DailyQuestRewardRecovery';
import NPCManager from './components/NPCManager';
import ErrorReports from './components/ErrorReports';
import Dashboard from './components/Dashboard';
import ChatManager from './components/ChatManager';
import PlayerActivityDashboard from './components/PlayerActivityDashboard';
import TraderCatalogManager from './components/TraderCatalogManager';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00e5ff', // Bright cyan
      light: '#62efff',
      dark: '#00b2cc',
    },
    secondary: {
      main: '#ff4081', // Hot pink
      light: '#ff79b0',
      dark: '#c60055',
    },
    error: {
      main: '#ff5252', // Bright red
      light: '#ff867f',
      dark: '#c50e29',
    },
    warning: {
      main: '#ffab00', // Bright orange
      light: '#ffdd4b',
      dark: '#c67c00',
    },
    info: {
      main: '#2196f3', // Bright blue
      light: '#6ec6ff',
      dark: '#0069c0',
    },
    success: {
      main: '#00e676', // Bright green
      light: '#66ffa6',
      dark: '#00b248',
    },
    background: {
      default: '#0a0a0a', // Deeper black
      paper: '#1a1a1a', // Dark gray
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.8)',
    },
    divider: 'rgba(0, 229, 255, 0.2)', // Cyan dividers
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            '& fieldset': {
              borderColor: 'rgba(0, 229, 255, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 229, 255, 0.6)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00e5ff',
              borderWidth: '2px',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.8)',
          },
          '& .MuiOutlinedInput-input': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#2a2a2a',
          color: '#ffffff',
          borderRadius: '8px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 229, 255, 0.3)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 229, 255, 0.6)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00e5ff',
            borderWidth: '2px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'linear-gradient(145deg, #1a1a1a 0%, #2a2a2a 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(0, 229, 255, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'linear-gradient(145deg, #1a1a1a 0%, #252525 100%)',
          borderRadius: '12px',
          border: '1px solid rgba(0, 229, 255, 0.2)',
          '&:hover': {
            border: '1px solid rgba(0, 229, 255, 0.4)',
            boxShadow: '0 8px 32px rgba(0, 229, 255, 0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 4px 16px rgba(0, 229, 255, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 229, 255, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 600,
        },
        colorError: {
          backgroundColor: '#ff5252',
          color: '#ffffff',
        },
        colorWarning: {
          backgroundColor: '#ffab00',
          color: '#000000',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0a0a',
          borderBottom: '2px solid rgba(0, 229, 255, 0.3)',
          boxShadow: '0 4px 20px rgba(0, 229, 255, 0.1)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'linear-gradient(145deg, #1a1a1a 0%, #252525 100%)',
          border: '1px solid rgba(0, 229, 255, 0.3)',
          borderRadius: '8px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(0, 229, 255, 0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 229, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(0, 229, 255, 0.3)',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: '#00e5ff',
            height: '3px',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            color: '#00e5ff',
          },
          '&:hover': {
            color: '#62efff',
          },
        },
      },
    },
  },
});

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <EnvironmentProvider>
        <AuthProvider>
          <UserProvider>
            <MoaniPriceProvider>
              <ChakraUIProvider>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route 
                  path="/*" 
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/player-activity" element={<PlayerActivityDashboard />} />
                          <Route path="/user-search" element={<UserSearch />} />
                          <Route path="/user/:userId/:tab?" element={<UserManagement />} />
                          <Route path="/assets" element={<AssetsManager />} />
                          <Route path="/mint-requests" element={<MintRequestsManager />} />
                          <Route path="/mint-pipeline" element={<MintPipelineManager />} />
                          <Route path="/mint-pipeline/contracts" element={<ContractsManager />} />
                          <Route path="/nft-metadata" element={<NftMetadataManager />} />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route path="/datapoint-activity" element={<DatapointActivity />} />
                          <Route path="/auth-logs" element={<AuthLogs />} />
                          <Route path="/events" element={<EventDashboard />} />
                          <Route path="/event-planner" element={<EventPlanner />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/admin-users" element={<AdminUserManagement />} />
                          <Route path="/wallets" element={<WalletManager />} />
                          <Route path="/security" element={<SecurityManager />} />
                          <Route path="/server-deploy" element={<ServerDeployManager />} />
                          <Route path="/stakes" element={<StakesManager />} />
                          <Route path="/auction-refunds" element={<AuctionRefunds />} />
                          <Route path="/drop-table-tester" element={<DropTableTester />} />
                          <Route path="/conversations" element={<ConversationsList />} />
                          <Route path="/conversations/:conversationId" element={<ConversationDetail />} />
                          <Route path="/allocations" element={<AllocationDashboard />} />
                          <Route path="/allocations/tracked-wallets" element={<TrackedWallets />} />
                          <Route path="/auction-farmers" element={<AuctionFarmers />} />
                          <Route path="/moani-blockchain-transfers" element={<MoaniBlockchainTransfers />} />
                          <Route path="/moani-transactions" element={<MoaniTransactions />} />
                          <Route path="/multi-account-detection" element={<MultiAccountDetection />} />
                          <Route path="/ip-grouped-users" element={<IPGroupedUsers />} />
                          <Route path="/quests" element={<QuestManager />} />
                          <Route path="/quests/daily-reward-recovery" element={<DailyQuestRewardRecovery />} />
                          <Route path="/npcs" element={<NPCManager />} />
                          <Route path="/trader-catalog" element={<TraderCatalogManager />} />
                          <Route path="/error-reports" element={<ErrorReports />} />
                          <Route path="/chat" element={<ChatManager />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  } 
                />
              </Routes>
              </Router>
              </ThemeProvider>
              </ChakraUIProvider>
            </MoaniPriceProvider>
          </UserProvider>
        </AuthProvider>
      </EnvironmentProvider>
    </QueryClientProvider>
  );
}

export default App; 